import type {
  ChannelSettings,
  ExecutionMode,
  ReasonCode,
  SaveSettingsRequest,
  Sizing,
} from '../../api';
import { type Features, isHiddenMode } from '../../lib/config/features-context';

import {
  compareDecimal,
  type ExactDecimal,
  isHalfTypedDecimal,
  lacksLeadingDigit,
  parseWireDecimal,
  readDecimal,
  wholeDecimal,
} from './exact-decimal';

/**
 * Confirmation delay in minutes for a channel that has none saved. Provisional until Q24: shown
 * with no edit control (M2 R10, review decision D15). A delay already saved with another value is
 * kept and shown as saved, never reset to this one (I7).
 */
export const CONFIRMATION_EXPIRY_MINUTES = 5;

/**
 * Highest fallback stop-loss distance in pips (M2 R20, provisional until Q46). The API refuses
 * the same bound with `VALIDATION_FAILED`; nothing is clamped to it.
 */
const MAX_FALLBACK_PIPS = 10_000;

/** Risk % and lot size take at most 2 decimals (M2 R20, provisional until Q46). */
const SIZE_DECIMALS = 2;

/** Highest risk %: 100 (M2 R20, provisional until Q46). */
const MAX_RISK_PERCENT = wholeDecimal(100);

/**
 * The E7 form as the trader fills it: raw text fields and nullable choices. `null` means "not
 * chosen": nothing is pre-selected on a channel that was never set up (I7, M2 R2).
 */
export interface SettingsDraft {
  readonly sizingMode: Sizing['mode'] | null;
  readonly riskPercent: string;
  readonly lots: string;
  /** Off unless the trader turns it on, or the saved version says so (M2 R7). */
  readonly copySignalsWithoutStopLoss: boolean;
  /** Starts empty, never a default distance (Q2, M2 R8). */
  readonly fallbackStopLossPips: string;
  readonly executionMode: ExecutionMode['mode'] | null;
  /**
   * Delay sent with the confirmation mode: the saved one when there is one, else
   * {@link CONFIRMATION_EXPIRY_MINUTES} (M2 R10, Q24). Never edited on E7 until Q24.
   */
  readonly expiryMinutes: number;
  /** Off (`null` on the wire) unless the trader turns it on (M2 R11). */
  readonly maxOpenPositionsEnabled: boolean;
  readonly maxOpenPositions: string;
}

/** Draft of a channel that was never set up: no choice made, every switch off (AC1). */
export const EMPTY_DRAFT: SettingsDraft = {
  sizingMode: null,
  riskPercent: '',
  lots: '',
  copySignalsWithoutStopLoss: false,
  fallbackStopLossPips: '',
  executionMode: null,
  expiryMinutes: CONFIRMATION_EXPIRY_MINUTES,
  maxOpenPositionsEnabled: false,
  maxOpenPositions: '',
};

/**
 * The form filled with a saved version, exactly as saved (the delay included, I7). A saved mode
 * the trader can no longer choose is left empty, never replaced by another (P8 R3, I7): the form
 * says why and Save waits for a choice.
 */
export function draftFromSettings(settings: ChannelSettings, features: Features): SettingsDraft {
  const { sizing, execution } = settings;
  return {
    sizingMode: sizing.mode,
    riskPercent: sizing.mode === 'RISK_PERCENT' ? sizing.riskPercent : '',
    lots: sizing.mode === 'FIXED_LOT' ? sizing.lots : '',
    copySignalsWithoutStopLoss: settings.copySignalsWithoutStopLoss,
    fallbackStopLossPips: settings.fallbackStopLossPips ?? '',
    executionMode: isHiddenMode(execution.mode, features) ? null : execution.mode,
    expiryMinutes:
      execution.mode === 'CONFIRMATION' ? execution.expiryMinutes : CONFIRMATION_EXPIRY_MINUTES,
    maxOpenPositionsEnabled: settings.maxOpenPositions !== null,
    maxOpenPositions: settings.maxOpenPositions === null ? '' : String(settings.maxOpenPositions),
  };
}

/**
 * The affiliate's recommendation applied on the trader's tap (M2 R14): size mode, value and
 * execution mode only. It only fills the form; the trader still sees the preview and saves (I7).
 * The confirmation delay stays the draft's: R14 does not list it, and M2 sends only the saved
 * delay or 5 until Q24. A recommended mode the trader cannot choose now is not applied: the draft
 * keeps its own mode (P8 R5, I7).
 */
export function applyRecommendation(
  draft: SettingsDraft,
  recommended: ChannelSettings,
  features: Features,
): SettingsDraft {
  const { sizing, execution } = recommended;
  return {
    ...draft,
    sizingMode: sizing.mode,
    riskPercent: sizing.mode === 'RISK_PERCENT' ? sizing.riskPercent : draft.riskPercent,
    lots: sizing.mode === 'FIXED_LOT' ? sizing.lots : draft.lots,
    executionMode: isHiddenMode(execution.mode, features) ? draft.executionMode : execution.mode,
  };
}

/** How {@link ChannelSettingsForm} is used (S7 R18): E7's default, or E5's first set-up. */
export type FormVariant = 'settings' | 'firstSetup';

/**
 * The draft a form starts from (S7 R5, R18). A saved version always wins, exactly as saved. With
 * none, the `firstSetup` variant (E5) starts from the affiliate's recommendation (size mode,
 * value and execution mode only, FXM-02 §4; every switch stays off, I7) and the `settings`
 * variant (E7) starts empty and offers the recommendation behind `[Apply]` (M2 R14). Without
 * either, nothing is pre-selected (M2 R2). A mode hidden by P8 is never pre-selected, whether
 * saved or recommended (P8 R3, R5). Pure: nothing is sent from here (S7 R7).
 */
export function initialDraft(
  active: ChannelSettings | null,
  recommended: ChannelSettings | null,
  variant: FormVariant,
  features: Features,
): SettingsDraft {
  if (active !== null) return draftFromSettings(active, features);
  if (variant === 'firstSetup' && recommended !== null) {
    return applyRecommendation(EMPTY_DRAFT, recommended, features);
  }
  return EMPTY_DRAFT;
}

/** A form field that can be wrong. */
type DraftField =
  | 'sizingMode'
  | 'riskPercent'
  | 'lots'
  | 'fallbackStopLossPips'
  | 'executionMode'
  | 'maxOpenPositions';

/**
 * What is wrong with a field: not filled in, still being typed ("0," or ",5": the form does not
 * mark it red, but Save stays disabled), or outside the bounds of M2 R20.
 */
type DraftProblem = 'required' | 'incomplete' | 'invalid';

/** One wrong field of the draft. */
export interface DraftIssue {
  readonly field: DraftField;
  readonly problem: DraftProblem;
  /** An incomplete number with no digit before its separator (",5"): the form says so. */
  readonly leadingDigit?: true;
  /** Reason code the API answers for the same body (M2 R8, R20). */
  readonly reasonCode: Extract<ReasonCode, 'VALIDATION_FAILED' | 'SETTINGS_FALLBACK_SL_REQUIRED'>;
}

/**
 * The settings the form builds: the `PUT …/settings` body without `expectedVersion`, which the
 * form adds from the version it shows (B-D16, C1 R18). Same keys as {@link ChannelSettings}.
 */
type SettingsValue = Omit<SaveSettingsRequest, 'expectedVersion'>;

/** Result of {@link validateSettingsDraft}. */
export type DraftValidation =
  | { readonly ok: true; readonly value: SettingsValue }
  | {
      readonly ok: false;
      /** Distinct reason codes, in field order (M2 spec §5 signature). */
      readonly reasonCodes: readonly ReasonCode[];
      /** One entry per wrong field, so the form can mark it (M2 §4 "Form incomplete"). */
      readonly issues: readonly DraftIssue[];
    };

type FieldCheck<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly problem: DraftProblem; readonly leadingDigit?: true };

/**
 * A decimal above 0 with at most `maxDecimals`, and at most `max` when given. Returns its wire
 * string, exactly as typed (a comma read as the separator): never rounded or clamped (I7, R20).
 */
function checkPositiveDecimal(
  text: string,
  bounds: { readonly maxDecimals: number; readonly max?: ExactDecimal },
): FieldCheck<string> {
  if (text.trim() === '') return { ok: false, problem: 'required' };
  if (lacksLeadingDigit(text)) return { ok: false, problem: 'incomplete', leadingDigit: true };
  if (isHalfTypedDecimal(text)) return { ok: false, problem: 'incomplete' };
  const read = readDecimal(text);
  if (
    read === null ||
    read.value.units === 0n ||
    read.value.scale > bounds.maxDecimals ||
    (bounds.max !== undefined && compareDecimal(read.value, bounds.max) > 0)
  ) {
    return { ok: false, problem: 'invalid' };
  }
  return { ok: true, value: read.wire };
}

/**
 * A whole number from `min` to `max`, typed with ASCII digits only. At most 15 digits: every
 * such number is exact as a JavaScript `Number` (below 2^53) and fits the integer part C1's
 * `DECIMAL_PATTERN` allows, so nothing is ever rounded on the way to the wire.
 */
function checkWhole(text: string, min: number, max: number): FieldCheck<number> {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: false, problem: 'required' };
  if (!/^\d{1,15}$/.test(trimmed)) return { ok: false, problem: 'invalid' };
  const value = Number(trimmed);
  return value >= min && value <= max ? { ok: true, value } : { ok: false, problem: 'invalid' };
}

/** The size field of the chosen mode and what it holds, or `null` before a mode is chosen. */
function checkSizing(
  draft: SettingsDraft,
): { readonly field: 'riskPercent' | 'lots'; readonly check: FieldCheck<Sizing> } | null {
  if (draft.sizingMode === 'RISK_PERCENT') {
    const risk = checkPositiveDecimal(draft.riskPercent, {
      maxDecimals: SIZE_DECIMALS,
      max: MAX_RISK_PERCENT,
    });
    return {
      field: 'riskPercent',
      check: risk.ok
        ? { ok: true, value: { mode: 'RISK_PERCENT', riskPercent: risk.value } }
        : risk,
    };
  }
  if (draft.sizingMode === 'FIXED_LOT') {
    // No upper bound on the lot in R20: the API refuses an excessive one with VALIDATION_FAILED,
    // and the preview marks the field (M2 R20, Q46).
    const lots = checkPositiveDecimal(draft.lots, { maxDecimals: SIZE_DECIMALS });
    return {
      field: 'lots',
      check: lots.ok ? { ok: true, value: { mode: 'FIXED_LOT', lots: lots.value } } : lots,
    };
  }
  return null;
}

/**
 * The sizing of the draft when it is complete and valid, else `null`. The preview is asked for
 * this value only (M2 R4): an incomplete size has no money example.
 */
export function draftSizing(draft: SettingsDraft): Sizing | null {
  const sizing = checkSizing(draft);
  return sizing?.check.ok === true ? sizing.check.value : null;
}

/** True when the fallback distance applies: risk mode with signals without SL copied (R8). */
export function needsFallbackStopLoss(draft: SettingsDraft): boolean {
  return draft.sizingMode === 'RISK_PERCENT' && draft.copySignalsWithoutStopLoss;
}

/**
 * The fallback distance the preview is asked for (P9 R6): the typed distance when it applies
 * ({@link needsFallbackStopLoss}) and is valid, else `null`. A distance being typed or refused
 * asks for no fallback example: no amount is shown for a value that cannot be saved (I7).
 */
export function draftFallbackStopLossPips(draft: SettingsDraft): string | null {
  if (!needsFallbackStopLoss(draft)) return null;
  const fallback = checkWhole(draft.fallbackStopLossPips, 1, MAX_FALLBACK_PIPS);
  return fallback.ok ? String(fallback.value) : null;
}

/**
 * Checks the whole form and builds the `PUT …/settings` body (M2 spec §5 "Pure core"). Every
 * key is written explicitly (C1 R11). Nothing is rounded, clamped or defaulted (I7, R20): a
 * wrong value is an issue, never a corrected value.
 */
export function validateSettingsDraft(draft: SettingsDraft): DraftValidation {
  const issues: DraftIssue[] = [];
  const fail = (field: DraftField, problem: DraftProblem, leadingDigit?: true): void => {
    const reasonCode =
      field === 'fallbackStopLossPips' && problem === 'required'
        ? 'SETTINGS_FALLBACK_SL_REQUIRED'
        : 'VALIDATION_FAILED';
    issues.push(
      leadingDigit === true
        ? { field, problem, reasonCode, leadingDigit }
        : { field, problem, reasonCode },
    );
  };

  let sizing: Sizing | null = null;
  const sizingCheck = checkSizing(draft);
  if (sizingCheck === null) fail('sizingMode', 'required');
  else if (sizingCheck.check.ok) sizing = sizingCheck.check.value;
  else fail(sizingCheck.field, sizingCheck.check.problem, sizingCheck.check.leadingDigit);

  let fallbackStopLossPips: string | null = null;
  if (needsFallbackStopLoss(draft)) {
    const fallback = checkWhole(draft.fallbackStopLossPips, 1, MAX_FALLBACK_PIPS);
    if (fallback.ok) fallbackStopLossPips = String(fallback.value);
    else fail('fallbackStopLossPips', fallback.problem);
  }

  if (draft.executionMode === null) fail('executionMode', 'required');

  let maxOpenPositions: number | null = null;
  if (draft.maxOpenPositionsEnabled) {
    const max = checkWhole(draft.maxOpenPositions, 1, Number.MAX_SAFE_INTEGER);
    if (max.ok) maxOpenPositions = max.value;
    else fail('maxOpenPositions', max.problem);
  }

  if (issues.length > 0 || sizing === null || draft.executionMode === null) {
    return { ok: false, reasonCodes: [...new Set(issues.map((i) => i.reasonCode))], issues };
  }
  return {
    ok: true,
    value: {
      sizing,
      copySignalsWithoutStopLoss: draft.copySignalsWithoutStopLoss,
      fallbackStopLossPips,
      execution:
        draft.executionMode === 'AUTOMATIC'
          ? { mode: 'AUTOMATIC' }
          : { mode: 'CONFIRMATION', expiryMinutes: draft.expiryMinutes },
      maxOpenPositions,
    },
  };
}

/** True when `field` holds a number with no digit before its separator (",5"). */
export function lacksDigitBeforeSeparator(
  issues: readonly DraftIssue[],
  field: DraftField,
): boolean {
  return issues.some((issue) => issue.field === field && issue.leadingDigit === true);
}

/** True when a typed value of `field` is wrong, so the field is marked (not while typing). */
export function hasInvalid(issues: readonly DraftIssue[], field: DraftField): boolean {
  return issues.some((issue) => issue.field === field && issue.problem === 'invalid');
}

/** Same size mode and same value as typed on the wire. */
export function sameSizing(a: Sizing, b: Sizing): boolean {
  if (a.mode === 'RISK_PERCENT')
    return b.mode === 'RISK_PERCENT' && a.riskPercent === b.riskPercent;
  return b.mode === 'FIXED_LOT' && a.lots === b.lots;
}

/** Same decimal value, whatever the writing ("0.10" and "0.1" are equal). */
function sameDecimal(a: string, b: string): boolean {
  return compareDecimal(parseWireDecimal(a), parseWireDecimal(b)) === 0;
}

/**
 * Same size mode and same value; unlike {@link sameSizing}, "0.10" equals "0.1". P11 R7 uses it
 * to tell a new size from the saved one.
 */
export function sameSizingValue(a: Sizing, b: Sizing): boolean {
  if (a.mode === 'RISK_PERCENT')
    return b.mode === 'RISK_PERCENT' && sameDecimal(a.riskPercent, b.riskPercent);
  return b.mode === 'FIXED_LOT' && sameDecimal(a.lots, b.lots);
}

/**
 * Same channel settings, field by field, decimals compared by value. Saving a body equal to the
 * active version would only add an identical version (M2 R16), so E7 does not. Values, not
 * texts: an API that writes "0.1" back for a saved "0.10" must not let the trader save the same
 * size again as a new version. The preview keeps the text comparison of {@link sameSizing}
 * (`isPreviewCurrent`): there, any difference only asks for one more preview.
 */
export function sameSettings(a: ChannelSettings, b: ChannelSettings): boolean {
  return (
    sameSizingValue(a.sizing, b.sizing) &&
    a.copySignalsWithoutStopLoss === b.copySignalsWithoutStopLoss &&
    (a.fallbackStopLossPips === null || b.fallbackStopLossPips === null
      ? a.fallbackStopLossPips === b.fallbackStopLossPips
      : sameDecimal(a.fallbackStopLossPips, b.fallbackStopLossPips)) &&
    a.execution.mode === b.execution.mode &&
    (a.execution.mode === 'AUTOMATIC' ||
      (b.execution.mode === 'CONFIRMATION' &&
        a.execution.expiryMinutes === b.execution.expiryMinutes)) &&
    a.maxOpenPositions === b.maxOpenPositions
  );
}
